import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, getDocs, query, orderBy } from "firebase/firestore";
import { CircularProgress } from "@mui/material";
import { generateSlug } from "../utils/slug";
import StructuredData from "./StructuredData";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";

const BlogComponent = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const firestore = getFirestore();
  const MAX_LENGTH = 200;
  const placeholderImage = "https://fakeimg.pl/600x400?text=No+Image&font=bebas";

  const truncateContent = (content, maxLength) => {
    if (!content) return "";
    const strippedContent = content.replace(/<[^>]*>/g, "");
    return strippedContent.length <= maxLength
        ? strippedContent
        : strippedContent.slice(0, maxLength) + '...';
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const blogCollection = collection(firestore, "blogs");
        const blogQuery = query(blogCollection, orderBy("createDate", "desc"));
        const blogSnapshot = await getDocs(blogQuery);

        const blogList = blogSnapshot.docs.map((doc) => {
          const blogData = doc.data();
          return {
            id: doc.id,
            ...blogData,
            formattedDate: blogData.createDate
                ? new Date(blogData.createDate.seconds * 1000).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })
                : "No Date"
          };
        });

        setBlogs(blogList);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [firestore]);

  const handleCardClick = (blog) => {
    const slug = blog.slug || generateSlug(blog.title);
    navigate(`/blog/${slug}`);
  };

  const blogListSchema = blogs.map((blog) => ({
    title: blog.title,
    url: `https://ezcarrentals.com.au/blog/${blog.slug || generateSlug(blog.title)}`,
  }));

  return (
    <>
      {!loading && blogs.length > 0 && (
        <StructuredData type="blogList" blogList={blogListSchema} />
      )}
      <Container maxWidth="xl" sx={{ marginTop: 15, marginBottom: 10 }}>
      <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: 5,
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '2.5rem'
          }}
      >
        Blogs
      </Typography>

        {loading ? (
            <Box sx={{ textAlign: "center", padding: "20px" }}>
          <CircularProgress />
        </Box>
        ) : (
            <Grid container spacing={4}>
          {blogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={blog.id}>
              <Card
                  className="blog-card"
                  onClick={() => handleCardClick(blog)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
              >
                <CardMedia
                    component="img"
                    alt={blog.title || "Blog image"}
                    height="200"
                    image={blog.imageUrl || placeholderImage}
                    sx={{
                      objectFit: 'cover',
                      width: '100%'
                    }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '3.5rem' // Ensures consistent height for 2 lines
                      }}
                  >
                    {blog.title}
                  </Typography>
                  <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                  >
                    {truncateContent(blog.content, MAX_LENGTH)}
                  </Typography>
                  <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'right' }}
                  >
                    {blog.formattedDate}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        )}
    </Container>
    </>
  );
};

export default BlogComponent;
